import { useEffect, useRef } from 'react'
import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react'

import Button from 'react-bootstrap/Button'
import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Col from 'react-bootstrap/Col'
import Dropdown from 'react-bootstrap/Dropdown'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import DeleteTaskModal from '../../components/DeleteTaskModal'
import DebugAccordion from '../../components/DebugAccordion'
import Icon from '../../components/Icon'
import LoadingAnimation from '../../components/LoadingAnimation'
import Loadable from '../../components/Loadable'
import PageTitle from '../../components/PageTitle.jsx'
import TimestampWithUTCTooltip from '../../components/TimestampWithUTCTooltip'
import UtgNav from './components/UtgNav'
import UtgTools from './components/UtgTools.jsx'
import VersionTooltip from '../../components/VersionTooltip.jsx'

import { fetchAgentManifest } from '../../state/actions/agentManifestActions.js'
import { fetchComponentConfig } from '../../state/actions/componentConfigActions.js'
import { fetchComponentConfigSchema } from '../../state/actions/componentConfigSchemaActions.js'
import { fetchMachineStatus } from '../../state/actions/machineStatusActions.js'
import { addNotification, NotificationTypes, notifyException } from '../../state/actions/notificationActions.js'
import { deleteTask } from '../../state/actions/taskActions.js'
import { fetchComponent, fetchUtg, fetchUtgDeviceList, fetchUtgTaskList, updateComponent } from '../../state/actions/utgActions.js'

import { agentManifestSignal, agentManifestLoadedSignal, agentManifestErrorSignal } from '../../state/signals/agentManifestSignals.js'
import { componentConfigSchemaLoadedSignal } from '../../state/signals/componentConfigSchemaSignals.js'
import { componentConfigSignal, componentConfigLoadedSignal, componentConfigErrorSignal } from '../../state/signals/componentConfigSignals.js'
import { machineStatusSignal, machineStatusLoadedSignal, machineStatusErrorSignal } from '../../state/signals/machineStatusSignals.js'
import { componentSignal, componentLoadedSignal, componentErrorSignal, utgSignal, utgLoadedSignal, utgTaskListSignal, utgTaskListLoadedSignal, utgDeviceListSignal, utgDeviceListLoadedSignal, utgErrorSignal } from '../../state/signals/utgSignals.js'

import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'
import { SortDirectionType } from '../../utils/constants.js'
import { computeSortedData } from '../../utils/table.js'
import { scrollToTop } from '../../utils/utils'
export default function UtgDetail() {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { beId, componentId } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/utgs`)
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewUtgs) {
            addNotification('You are not authorized to view UTGs for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(
        () => {
            return userPermissionsSummary?.value?.canEditUtgs
        }
    )

    useEffect(
        () => {
            if (beIdChanged) return
            fetchComponent(beId, componentId, config, accessToken)
            fetchUtgTaskList(beId, componentId, config, accessToken)
            fetchUtgDeviceList(beId, componentId, config, accessToken)
            fetchAgentManifest(config, accessToken)
            fetchUtg(beId, componentId, config, accessToken).then(
                () => {
                    fetchComponentConfigSchema(beId, utgSignal.value.utgVersion, config, accessToken)
                    fetchMachineStatus(beId, utgSignal.value.machinePermanentId, config, accessToken)
                    fetchComponentConfig(beId, utgSignal.value.machinePermanentId, utgSignal.value.componentPermanentId, config, accessToken)
                }
            )
        }, [beId, beIdChanged, componentId, config, accessToken]
    )

    const utgConnectionDataSignal = useSignal({})

    const formRef = useRef(null)
    const utgNicknameSignal = useSignal('')
    const utgMerchantSignal = useSignal('')
    const utgLocationSignal = useSignal('')
    const utgFileIdSignal = useSignal('')
    const utgLaneSignal = useSignal('')
    useSignalEffect(
        () => {
            if (!utgLoadedSignal.value) {
                utgNicknameSignal.value = 'Loading...'
                utgMerchantSignal.value = 'Loading...'
                utgLocationSignal.value = 'Loading...'
                utgFileIdSignal.value = 'Loading...'
                utgLaneSignal.value = 'Loading...'
                utgConnectionDataSignal.value = {
                    In4mListenerAddress: "Loading...",
                    In4mListenerPort: "Loading...",
                    UtgExeFilePath: "Loading..."
                }
            } else {
                utgNicknameSignal.value = utgSignal.value.componentNickname
                utgMerchantSignal.value = utgSignal.value.componentMerchant
                utgLocationSignal.value = utgSignal.value.componentLocation
                utgFileIdSignal.value = utgSignal.value.componentFileId
                utgLaneSignal.value = utgSignal.value.componentLane
                // this is an embedded json string within the utg json. Extract it so we can display
                utgConnectionDataSignal.value =
                    JSON.parse(utgSignal.value.componentConnectionString)
            }
        }
    )

    const tasksSortColumnSignal = useSignal('')
    const devicesSortColumnSignal = useSignal('')

    const tasksSortDirectionSignal = useSignal(SortDirectionType.DESCENDING)
    const devicesSortDirectionSignal = useSignal(SortDirectionType.DESCENDING)

    const tasksSortedDataComputed = useComputed(computeSortedData(utgTaskListSignal, tasksSortColumnSignal, tasksSortDirectionSignal))
    const devicesSortedDataComputed = useComputed(computeSortedData(utgDeviceListSignal, devicesSortColumnSignal, devicesSortDirectionSignal))

    const handleTasksHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == tasksSortColumnSignal.value) {
            tasksSortDirectionSignal.value = tasksSortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        tasksSortColumnSignal.value = datafield
    }
    const handleDevicesHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == devicesSortColumnSignal.value) {
            devicesSortDirectionSignal.value = devicesSortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        devicesSortColumnSignal.value = datafield
    }

    const handleInput = (event, signalVariable) => {
        signalVariable.value = event.currentTarget.value
    }

    const accordionData = [
        { name: 'UTG Data', data: utgSignal.value },
        { name: 'Component', data: componentSignal.value },
        //{ name: 'Utg Connection Data', data: utgConnectionDataComputed.value },
        { name: 'Component Config ', data: componentConfigSignal.value },
        { name: 'Machine Status', data: machineStatusSignal.value },
        { name: 'Devices', data: utgDeviceListSignal.value },
        { name: 'Tasks', data: utgTaskListSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    const handleSave = (event) => {
        event.preventDefault()
        event.stopPropagation()

        // deep clone the component data
        let clonedData = structuredClone(componentSignal.value)
        clonedData.nickname = utgNicknameSignal.value
        clonedData.merchant = utgMerchantSignal.value
        clonedData.location = utgLocationSignal.value
        clonedData.fileId = utgFileIdSignal.value
        clonedData.lane = utgLaneSignal.value
        //console.log(JSON.stringify(clonedData))

        // update and display a notification
        updateComponent(beId, clonedData, config, accessToken).then(
            (component) => {
                addNotification(`Successfully updated component "${component.nickname}"`, NotificationTypes.SUCCESS, true)
                scrollToTop("main")
                // navigate(0)
            }
        ).catch(error => {
            console.log(error.toString())
            addNotification('Unable to update the data.', NotificationTypes.ERROR, false)
            return false
        }
        )
    }

    const showRemoveTaskModalSignal = useSignal(false)
    const selectedTaskToDeleteSignal = useSignal([])
    const handleCancelRemoveTask = () => showRemoveTaskModalSignal.value = false
    const handleSubmitModalRemoveTask = () => {
        utgTaskListLoadedSignal.value = false
        showRemoveTaskModalSignal.value = false

        let promises = []
        for (let i = 0; i < selectedTaskToDeleteSignal.value.length; i++) {
            try {
                promises.push(deleteTask(selectedTaskToDeleteSignal.value[i].businessEntityId, selectedTaskToDeleteSignal.value[i].machinePermanentId, selectedTaskToDeleteSignal.value[i].taskId, config, accessToken))
            } catch (error) {
                addNotification(`Unable to remove all selected Tasks`, NotificationTypes.ERROR, false, 'Remove Tasks Failed')
            }
        }
        Promise.all(promises)
            .then(async () => {
                addNotification('The selected Tasks were successfully removed.', NotificationTypes.SUCCESS, false)
                fetchUtgTaskList(beId, componentId, config, accessToken)
            })
            .catch(() => {
                addNotification(`Unable to remove all selected Tasks`, NotificationTypes.ERROR, false, 'Remove Tasks Failed')
            })
    }

    const handleRemoveTask = (item) => {
        if (item) {
            selectedTaskToDeleteSignal.value = [item]
            showRemoveTaskModalSignal.value = true
        }
    }

    const handleCopyClipboard = item => {
        if (item) {
            // place the data on the users clipboard if the browser supports it
            if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(JSON.stringify(item, null, 2));
                addNotification(`The data for ${item.componentNickname} has been copied to your clipboard.`, NotificationTypes.SUCCESS, false, 'Copy Succeeded')
            }
            else {
                addNotification('This browser does not support copying data to the clipboard.', NotificationTypes.INFO, false, 'Copy Failed')
            }
        }
    }

    function DeviceTableRows({ items }) {
        if (items && items.length) {
            const rows = items.map(item => {
                return <DeviceTableRow item={item} key={`${item.deviceConfigId}`} />
            })

            return (
                <tbody>{rows}</tbody>
            )
        }
        else if (items) {
            return (
                <tbody><tr><td colSpan="9" className="text-center">This UTG does not have any devices.</td></tr></tbody>
            )
        }
    }

    function DeviceTableRow({ item }) {
        return (
            <tr>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions"><Icon icon="gear" width="16" height="16"></Icon></Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleCopyClipboard(item)}>Copy Row as JSON</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td>{item.componentNickname}</td>
                <td>{item.deviceStatusMachineName}</td>
                <td>{item.deviceStatusFirmwareVersion}</td>
                <td>...</td>
                <td>{item.deviceStatusDeviceSerialNumber}</td>
                <td>{item.deviceConfigDeviceType}</td>
                <td>{item.deviceConfigApiTerminalId}</td>
                <td>{item.deviceStatusDevicePciVersion}</td>
            </tr>
        )
    }

    function TaskTableRows({ items }) {
        if (items && items.length) {
            const rows = items.map(item => {
                return <TaskTableRow item={item} key={`${item.taskId}`} />
            })

            return (
                <tbody>{rows}</tbody>
            )
        }
        else if (items) {
            return (
                <tbody><tr><td colSpan="6" className="text-center">This UTG does not have any tasks.</td></tr></tbody>
            )
        }
    }

    function TaskTableRow({ item }) {
        return (
            <tr>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions">
                            <Icon icon="gear" width="16" height="16"></Icon>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleCopyClipboard(item)}>Copy Row as JSON</Dropdown.Item>
                            {
                                userPermissionsSummary.value?.canEditUtgs && <Dropdown.Item onClick={() => handleRemoveTask(item)}>Delete Task</Dropdown.Item>
                            }
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td>{item.taskTypeName}</td>
                <td>{item.taskStatusName}</td>
                <td><TimestampWithUTCTooltip>{item.taskLastUpdatedAt}</TimestampWithUTCTooltip></td>
                <td><TimestampWithUTCTooltip>{item.taskRunAfter}</TimestampWithUTCTooltip></td>
                <td>{item.taskBulkLabel}</td>
            </tr>
        )
    }

    function TableColumnHeader({ title, sortable, datafield, sortColumn, sortDirection, handleHeaderTableClick }) {
        // non-sortable columns do not have click handlers
        if (!sortable) {
            return (
                <th>{title}</th>
            )
        }

        // sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
        if (datafield != sortColumn.value) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
            )
        }

        // datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
        if (sortDirection.value == SortDirectionType.DESCENDING) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                    <Icon icon="caret-down" width="16" height="16"></Icon>
                </th>
            )
        }

        // else must be ascending
        return (
            <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                <Icon icon="caret-up" width="16" height="16"></Icon>
            </th>
        )
    }

    return (
        <>
            <DeleteTaskModal
                show={showRemoveTaskModalSignal.value}
                handleCancel={handleCancelRemoveTask}
                handleConfirm={handleSubmitModalRemoveTask}
                selectedTasks={selectedTaskToDeleteSignal}
            />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/utgs` }}>
                    UTG List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{
                    utgLoadedSignal.value ? (<>Manage {utgSignal.value.componentNickname}</>) : "Loading..."
                }</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title={utgLoadedSignal.value ? `Manage UTG ${utgSignal.value.componentNickname}` : "Loading..."} canEditSignal={canEditComputed} />
                <Loadable loadedSignals={[componentConfigLoadedSignal]} errorSignals={[componentConfigErrorSignal]}>
                    <UtgTools componentConfigData={componentConfigSignal.value}></UtgTools>
                </Loadable>
            </Stack>
            <UtgNav activeKey="1"></UtgNav>

            <Form ref={formRef} validated onSubmit={(event) => handleSave(event)}>

                <Form.Group as={Row} className="mb-3" controlId="ComponentNickname">
                    <Form.Label column sm="2" className="text-end">Component Nickname/IP:</Form.Label>
                    <Form.Control.Feedback>Required</Form.Control.Feedback>
                    <Col sm="10">
                        <Stack>
                            <Form.Control
                                plaintext={!userPermissionsSummary?.value?.canEditUtgs}
                                defaultValue={utgNicknameSignal.value}
                                onChange={
                                    (event) => handleInput(event, utgNicknameSignal)
                                }
                                required={userPermissionsSummary?.value?.canEditUtgs}
                                type="text"
                                disabled={!utgLoadedSignal.value}
                            />
                            <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
                        </Stack>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="ComponentMerchant">
                    <Form.Label column sm="2" className="text-end">Merchant Description:</Form.Label>
                    <Col sm="10">
                        <Stack>
                            <Form.Control
                                plaintext={!userPermissionsSummary?.value?.canEditUtgs}
                                defaultValue={utgMerchantSignal.value}
                                onChange={
                                    (event) => handleInput(event, utgMerchantSignal)
                                }
                                required={userPermissionsSummary?.value?.canEditUtgs}
                                type="text"
                                disabled={!utgLoadedSignal.value}
                            />
                            <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
                        </Stack>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="ComponentLocation">
                    <Form.Label column sm="2" className="text-end">Location:</Form.Label>
                    <Col sm="10">
                        <Stack>
                            <Form.Control
                                plaintext={!userPermissionsSummary?.value?.canEditUtgs}
                                defaultValue={utgLocationSignal.value}
                                onChange={
                                    (event) => handleInput(event, utgLocationSignal)
                                }
                                required={userPermissionsSummary?.value?.canEditUtgs}
                                type="text"
                                disabled={!utgLoadedSignal.value}
                            />
                            <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
                        </Stack>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="ComponentLane">
                    <Form.Label column sm="2" className="text-end">Lane:</Form.Label>
                    <Col sm="10">
                        <Form.Control
                            plaintext={!userPermissionsSummary?.value?.canEditUtgs}
                            defaultValue={utgLaneSignal.value}
                            onChange={
                                (event) => handleInput(event, utgLaneSignal)
                            }
                            type="text"
                            disabled={!utgLoadedSignal.value}
                        />
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="ComponentFileId">
                    <Form.Label column sm="2" className="text-end">File ID / MID:</Form.Label>
                    <Col sm="10">
                        <Stack>
                            <Form.Control
                                plaintext={!userPermissionsSummary?.value?.canEditUtgs}
                                defaultValue={utgFileIdSignal.value}
                                onChange={
                                    (event) => handleInput(event, utgFileIdSignal)
                                }
                                required={userPermissionsSummary?.value?.canEditUtgs}
                                type="text"
                                disabled={!utgLoadedSignal.value}
                            />
                            <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
                        </Stack>
                    </Col>
                </Form.Group>

                {userPermissionsSummary?.value?.canEditUtgs && <Form.Group as={Row} className="mb-3" controlId="formUpdateButton">
                    <Form.Label column sm="2" className="text-end"></Form.Label>
                    <Col sm="10">
                        <Stack direction="horizontal" gap={3}>
                            <Button type="submit">Update</Button>
                            <Button variant="secondary" onClick={() => navigate(0)}>Cancel</Button>
                        </Stack>
                    </Col>
                </Form.Group>}

                <hr></hr>

                <Form.Group as={Row} className="mb-3" controlId="MachineStatus">
                    <Form.Label column sm="2" className="text-end">Machine Status:</Form.Label>
                    <Col sm="10">
                        <Loadable loadedSignals={[utgLoadedSignal]} errorSignals={[utgErrorSignal]}>
                            <Form.Control plaintext readOnly defaultValue={utgSignal.value?.machineStatus} />
                        </Loadable>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="MachineStatusEntityLastKnownCommunication">
                    <Form.Label column sm="2" className="text-end">Last Known Communication:</Form.Label>
                    <Col sm="10">
                        <Loadable loadedSignals={[utgLoadedSignal]} errorSignals={[utgErrorSignal]}>
                            <Form.Control
                                plaintext
                                readOnly
                                defaultValue={
                                    new Date(utgSignal.value.machineStatusEntityLastKnownCommunication).toLocaleString()
                                }
                            />
                        </Loadable>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="MachineStatusEntityNextPredictedCommunication">
                    <Form.Label column sm="2" className="text-end">Next Predicted Communication:</Form.Label>
                    <Col sm="10">
                        <Loadable loadedSignals={[utgLoadedSignal]} errorSignals={[utgErrorSignal]}>
                            <Form.Control
                                plaintext
                                readOnly
                                defaultValue={
                                    new Date(utgSignal.value.machineStatusEntityNextPredictedCommunication).toLocaleString()
                                }
                            />
                        </Loadable>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="MachineStatusStandardTimeZone">
                    <Form.Label column sm="2" className="text-end">Time Zone:</Form.Label>
                    <Col sm="10">
                        <Loadable loadedSignals={[utgLoadedSignal]} errorSignals={[utgErrorSignal]}>
                            <Form.Control
                                plaintext
                                readOnly
                                defaultValue={utgSignal.value?.machineStatusEntityStandardTimeZone}
                            />
                        </Loadable>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="AgentVersion">
                    <Form.Label column sm="2" className="text-end">Agent Version:</Form.Label>
                    <Loadable loadedSignals={[agentManifestLoadedSignal]} errorSignals={[agentManifestErrorSignal]}>
                        <VersionTooltip
                            version={utgLoadedSignal.value ? utgSignal.value.agentVersion : "Loading..."}
                            availableVersions={agentManifestSignal.value}
                            context="Agent"
                        />
                    </Loadable>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="UtgVersion">
                    <Form.Label column sm="2" className="text-end">UTG Version:</Form.Label>
                    <Col sm="10">
                        <Form.Control plaintext readOnly defaultValue={utgSignal.value?.utgVersion} />
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="MachineStatusWindowsVersion">
                    <Form.Label column sm="2" className="text-end">Windows Version:</Form.Label>
                    <Col sm="10">
                        <Loadable loadedSignals={[utgLoadedSignal]} errorSignals={[utgErrorSignal]}>
                            <Form.Control plaintext readOnly defaultValue={utgSignal.value?.machineStatusEntityWindowsVersion} />
                        </Loadable>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="ConfigStatus">
                    <Form.Label column sm="2" className="text-end">Configuration Status:</Form.Label>
                    <Col sm="10">
                        <Loadable loadedSignals={[utgLoadedSignal]} errorSignals={[utgErrorSignal]}>
                            <Form.Control plaintext readOnly defaultValue={utgLoadedSignal.value?.configStatus} />
                        </Loadable>
                    </Col>
                </Form.Group>
                {/*<Form.Group as={Row} className="mb-3" controlId="formParentAccount">
                    <Form.Label column sm="2" className="text-end">Parent Account:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue="" /></Col>
                </Form.Group>*/}
                <Form.Group as={Row} className="mb-3" controlId="formConnectionIn4mListenerAddress">
                    <Form.Label column sm="2" className="text-end">Shift4 Command Center Listener IP:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue={utgConnectionDataSignal.value?.In4mListenerAddress} /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="formConnectionIn4mListenerPort">
                    <Form.Label column sm="2" className="text-end">Shift4 Command Center Listener Port:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue={utgConnectionDataSignal.value?.In4mListenerPort} /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="formConnectionUtgExeFilePath">
                    <Form.Label column sm="2" className="text-end">UTG Path:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue={utgConnectionDataSignal.value?.UtgExeFilePath} /></Col>
                </Form.Group>
            </Form >
            <hr></hr>

            <h2 className="display-4 fs-4 px-3 my-3 mt-5">Devices</h2>
            <Table responsive striped size="sm">
                <thead>
                    <tr className="fw-bold">
                        <TableColumnHeader title={'Action'} sortable={false} datafield={''} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Service Name'} sortable={true} datafield={'componentNickname'} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Machine Name'} sortable={true} datafield={'deviceStatusMachineName'} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Device Firmware'} sortable={true} datafield={'deviceStatusFirmwareVersion'} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Device Keys'} sortable={false} datafield={''} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Device Serial'} sortable={true} datafield={'deviceStatusDeviceSerialNumber'} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Device Type'} sortable={true} datafield={'deviceConfigDeviceType'} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Terminal ID'} sortable={true} datafield={'deviceConfigApiTerminalId'} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'PCI Version'} sortable={true} datafield={'deviceStatusDevicePciVersion'} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                    </tr>
                </thead>
                {
                    utgDeviceListLoadedSignal.value ? (
                        <DeviceTableRows items={devicesSortedDataComputed.value} />
                    ) : (<tbody><tr><td colSpan={9}><LoadingAnimation title="Loading device list..." /></td></tr></tbody>)
                }
            </Table>

            <h2 className="display-4 fs-4 px-3 my-3 mt-5">Scheduled Tasks</h2>
            <Table responsive striped hover size="sm">
                <thead>
                    <tr className="fw-bold">

                        <TableColumnHeader title={'Action'} sortable={false} datafield={''} sortColumn={tasksSortColumnSignal} sortDirection={tasksSortDirectionSignal} handleHeaderTableClick={handleTasksHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Type'} sortable={true} datafield={'taskTypeName'} sortColumn={tasksSortColumnSignal} sortDirection={tasksSortDirectionSignal} handleHeaderTableClick={handleTasksHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Status'} sortable={true} datafield={'taskStatusName'} sortColumn={tasksSortColumnSignal} sortDirection={tasksSortDirectionSignal} handleHeaderTableClick={handleTasksHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Last updated'} sortable={true} datafield={'taskLastUpdatedAt'} sortColumn={tasksSortColumnSignal} sortDirection={tasksSortDirectionSignal} handleHeaderTableClick={handleTasksHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Scheduled time'} sortable={true} datafield={"taskRunAfter"} sortColumn={tasksSortColumnSignal} sortDirection={tasksSortDirectionSignal} handleHeaderTableClick={handleTasksHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Task Label'} sortable={true} datafield={'taskBulkLabel'} sortColumn={tasksSortColumnSignal} sortDirection={tasksSortDirectionSignal} handleHeaderTableClick={handleTasksHeaderTableClick}></TableColumnHeader>
                    </tr>
                </thead>
                {
                    utgTaskListLoadedSignal.value ? (
                        <TaskTableRows items={tasksSortedDataComputed.value} />
                    ) : (<tbody><tr><td colSpan={6}><LoadingAnimation title="Loading tasks..." /></td></tr></tbody>)
                }
            </Table>

            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}